import { subscribeToLocation } from "@swan-io/chicane";
import { Fragment, useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { P, match } from "ts-pattern";
import { applications } from "../utils/applications";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { useQuery } from "../utils/graphql";
import { t } from "../utils/i18n";
import { hasBillingAccess, hasSettingsAccess } from "../utils/permissions";
import { MeQuery, OrganizationQuery } from "../utils/queries";
import { RouteArea, router } from "../utils/routes";
import { UserAvatar } from "./Avatar";
import { Icon } from "./Icon";
import { OrganizationSelect } from "./OrganizationSelect";
import { Popover, PopoverContent, PopoverSeparator } from "./Popover";
import { Fill, Space } from "./Space";
import { PressableLink, Text } from "./Text";

const styles = StyleSheet.create({
  sidebar: {
    width: 256,
    flexGrow: 0,
    borderRightWidth: 1,
    borderColor: colors.neutral[700],
  },
  sidebarContent: {
    height: "100%",
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: colors.neutral[900],
  },

  background: {
    position: "absolute",
    top: -2000,
    left: -3000,
    right: 0,
    bottom: 0,
    backgroundImage: "linear-gradient(327.67deg, rgba(14, 14, 14, 0) 7.72%, #FFC799 238.02%)",
    pointerEvents: "none",
  },

  linksTitle: {
    paddingHorizontal: 12,
  },

  sidebarLink: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: radii[4],
    backgroundColor: invariantColor.transparent,
    transitionDuration: animDuration[200],
    transitionProperty: "background-color",
  },
  sidebarLinkActive: {
    backgroundColor: colors.primary[900],
  },
  sidebarLinkHover: {
    backgroundColor: colors.neutral[700],
  },

  popoverButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: colors.neutral[800],
    borderWidth: 1,
    borderColor: colors.neutral[700],
    borderRadius: radii[4],
    transitionDuration: animDuration[200],
    transitionProperty: "background-color",
    outlineWidth: 0,
  },
  popoverButtonHover: {
    backgroundColor: colors.neutral[700],
  },

  fill: {
    flex: 1,
  },

  accountInfo: {
    flexDirection: "row",
    alignItems: "center",
  },

  accountLink: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: radii[4],
    transitionDuration: animDuration[200],
    transitionProperty: "background-color",
    outlineWidth: 0,
  },
  accountLinkHover: {
    backgroundColor: colors.neutral[600],
  },
});

export const Sidebar: React.FC = () => {
  const route = router.useRoute(["OrgArea", "OrgList"]);

  return (
    <ScrollView style={styles.sidebar} contentContainerStyle={styles.sidebarContent}>
      <View style={styles.background} />

      {match(route)
        .with({ name: "OrgArea" }, ({ params: { org_slug } }) => (
          <>
            <OrganizationSelect orgSlug={org_slug} />
            <Space height={36} />
            <GeneralSection orgSlug={org_slug} />
            <Space height={36} />
            <AppsSection orgSlug={org_slug} />
          </>
        ))
        .with({ name: "OrgList" }, () => null)
        .with(P.nullish, () => <OrganizationSelect />)
        .exhaustive()}

      <Fill minHeight={12} />

      <UserButton />
    </ScrollView>
  );
};

type SidebarLinkProps = {
  to: string;
  areaName?: RouteArea;
  icon: IconName;
  label: string;
};

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, areaName, icon, label }) => {
  return (
    <PressableLink
      to={to}
      areaName={areaName}
      style={({ pressed, focused, hovered, active }) => [
        styles.sidebarLink,
        (pressed || focused || hovered) && styles.sidebarLinkHover,
        active && styles.sidebarLinkActive,
      ]}
    >
      {({ active }) => (
        <>
          <Icon name={icon} size={16} color={colors.primary[500]} />
          <Space width={12} />
          <Text variant="smallFlat" color={active ? colors.primary[500] : invariantColor.white}>
            {label}
          </Text>
        </>
      )}
    </PressableLink>
  );
};

type GeneralSectionProps = {
  orgSlug: string;
};

export const GeneralSection: React.FC<GeneralSectionProps> = ({ orgSlug }) => {
  const {
    data: { organization },
  } = useQuery(OrganizationQuery, { orgSlug });
  const isCompany = organization.type === "COMPANY";

  return (
    <>
      <Text variant="smallMono" style={styles.linksTitle}>
        {t("sidebar.general")}
      </Text>

      <Space height={12} />

      <SidebarLink
        to={router.OrgHome({ org_slug: orgSlug })}
        icon="house"
        label={t("sidebar.home")}
      />
      {isCompany && (
        <>
          <Space height={2} />
          <SidebarLink
            to={router.OrgMembersIndex({ org_slug: orgSlug })}
            areaName="OrgMembersArea"
            icon="users"
            label={t("sidebar.members")}
          />
          <Space height={2} />
          <SidebarLink
            to={router.OrgTeamsIndex({ org_slug: orgSlug })}
            areaName="OrgTeamsArea"
            icon="users_group"
            label={t("sidebar.teams")}
          />
        </>
      )}
      {hasSettingsAccess(organization.permissions) && (
        <>
          <Space height={2} />
          <SidebarLink
            to={router.OrgSettings({ org_slug: orgSlug })}
            icon="organization"
            label={t("sidebar.organization")}
          />
        </>
      )}
      {hasBillingAccess(organization.permissions) && (
        <>
          <Space height={2} />
          <SidebarLink
            to={router.OrgPlans({ org_slug: orgSlug })}
            icon="credit_card"
            label={t("sidebar.plans")}
          />
        </>
      )}
      {hasBillingAccess(organization.permissions) && (
        <>
          <Space height={2} />
          <SidebarLink
            to={router.OrgBilling({ org_slug: orgSlug })}
            icon="file_document"
            label={t("sidebar.billing")}
          />
        </>
      )}
    </>
  );
};

export const AppsSection: React.FC<GeneralSectionProps> = ({ orgSlug }) => {
  return (
    <>
      <Text variant="smallMono" style={styles.linksTitle}>
        {t("sidebar.apps")}
      </Text>

      <Space height={12} />

      {Object.entries(applications).map(([app, appInfo], index) => (
        <Fragment key={app}>
          {index > 0 && <Space height={2} />}

          <SidebarLink
            to={router.OrgApp({ app_slug: app, org_slug: orgSlug })}
            icon={appInfo.icon}
            label={appInfo.name}
          />
        </Fragment>
      ))}
    </>
  );
};

const UserButton: React.FC = () => {
  const ref = useRef<View | null>(null);
  const { data } = useQuery(MeQuery);
  const { fullName, email, avatarUrl, updatedAt } = data.me;

  const [opened, setOpened] = useState(false);

  const togglePopover = () => {
    setOpened((prev) => !prev);
  };

  const closePopover = () => {
    setOpened(false);
  };

  useEffect(() => {
    const unsubscribe = subscribeToLocation(closePopover);
    return unsubscribe;
  }, []);

  return (
    <>
      <Pressable
        ref={ref}
        style={({ pressed, focused, hovered }) => [
          styles.popoverButton,
          (pressed || focused || hovered) && styles.popoverButtonHover,
        ]}
        onPress={togglePopover}
      >
        <UserAvatar size="regular" name={fullName} url={avatarUrl} updatedAt={updatedAt} />

        <Space width={12} />

        <Text variant="body" color={invariantColor.white} ellipsis={true}>
          {fullName}
        </Text>

        <Fill minWidth={12} />

        <Icon name="unfold" size={20} color={colors.neutral[600]} />
      </Pressable>

      <Popover
        target={ref}
        opened={opened}
        placement="top"
        offset={12}
        fitTargetWidth={true}
        onClose={closePopover}
      >
        <PopoverContent paddingHorizontal={16} paddingVertical={16} style={styles.accountInfo}>
          <UserAvatar size="medium" name={fullName} url={avatarUrl} updatedAt={updatedAt} />

          <Space width={12} />

          <View style={styles.fill}>
            <Text variant="bodyFlat" color={invariantColor.white}>
              {fullName}
            </Text>

            <Space height={4} />

            <Text variant="smallFlat" ellipsis={true}>
              {email}
            </Text>
          </View>
        </PopoverContent>

        <PopoverSeparator />

        <PopoverContent>
          <PressableLink
            to={router.MyProfile()}
            style={({ pressed, hovered, focused }) => [
              styles.accountLink,
              (pressed || hovered || focused) && styles.accountLinkHover,
            ]}
            variant="smallFlat"
            color={invariantColor.white}
          >
            {t("sidebar.my_profile")}
          </PressableLink>

          <PressableLink
            to={router.Signout()}
            style={({ pressed, hovered, focused }) => [
              styles.accountLink,
              (pressed || hovered || focused) && styles.accountLinkHover,
            ]}
            variant="smallFlat"
            color={colors.negative[500]}
          >
            {t("sidebar.sign_out")}
          </PressableLink>
        </PopoverContent>
      </Popover>
    </>
  );
};
